import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { tasksAtom } from '../../../../../atoms/v0/taskAtoms';
import { IOSModal } from '../../ios/IOSModal';
import { startLoopingSound, stopLoopingSound, startContinuousVibration, stopContinuousVibration, stopAllTimerNotifications } from '../../../../../services/timerNotification';

interface TimerAlarmModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId?: string; // 关联任务ID
  timerDuration: number; // 计时时长(分钟)
  timerDurationSeconds: number; // 计时时长(秒)
  soundFile: 'vivora' | 'delay' | 'sms'; // 音频文件
  enableSound: boolean; // 是否启用声音
  enableVibration: boolean; // 是否启用震动
}

/**
 * 计时器闹钟模态框组件
 * 当计时器结束时显示，提供持续的音频和震动提醒直到用户确认
 */
export const TimerAlarmModal: React.FC<TimerAlarmModalProps> = ({
  isOpen,
  onClose,
  taskId,
  timerDuration,
  timerDurationSeconds,
  soundFile,
  enableSound,
  enableVibration,
}) => {
  const { t } = useTranslation(['v3/clock']);
  const tasks = useAtomValue(tasksAtom);
  
  // 获取关联任务信息
  const linkedTask = taskId ? tasks.find(task => task.id === taskId) : null;
  
  // 格式化计时时长显示
  const formatDuration = () => {
    if (timerDurationSeconds > 0) {
      return `${timerDuration} 分 ${timerDurationSeconds} 秒`;
    }
    return `${timerDuration} 分钟`;
  };
  
  // 处理确认关闭
  const handleConfirm = () => {
    stopAllTimerNotifications();
    onClose();
  };
  
  // 启动和停止通知
  useEffect(() => {
    const startNotifications = async () => {
      if (isOpen) {
        // 启动通知
        if (enableSound) {
          startLoopingSound(soundFile);
        }
        if (enableVibration) {
          await startContinuousVibration();
        }
      } else {
        // 停止通知
        stopAllTimerNotifications();
      }
    };

    startNotifications();

    // 清理函数：组件卸载时停止所有通知
    return () => {
      stopAllTimerNotifications();
    };
  }, [isOpen, enableSound, enableVibration, soundFile]);
  
  return (
    <IOSModal
      isOpen={isOpen}
      onClose={handleConfirm}
      title="⏰"
      buttons={[
        {
          text: t('v3/clock:alarm.confirm', '知道了'),
          onClick: handleConfirm,
          variant: 'primary'
        }
      ]}
    >
      <div className="text-center space-y-4 py-4">
        {/* 关联任务信息 */}
        {linkedTask && (
          <div className="flex items-center justify-center gap-2 text-ios-body">
            <span className="text-lg">📋</span>
            <span className="font-medium">{linkedTask.title}</span>
          </div>
        )}
        
        {/* 计时信息 */}
        <div className="flex items-center justify-center gap-2 text-ios-body text-ios-gray dark:text-ios-dark-gray">
          <span className="text-lg">⏱️</span>
          <span>{t('v3/clock:alarm.duration', '计时 {{duration}}', { duration: formatDuration() })}</span>
        </div>
        
        {/* 提醒状态 */}
        <div className="text-ios-caption text-ios-gray dark:text-ios-dark-gray">
          {enableSound && enableVibration && (
            <span>🔔 {t('v3/clock:alarm.soundAndVibration', '正在播放提醒铃声和震动...')}</span>
          )}
          {enableSound && !enableVibration && (
            <span>🔔 {t('v3/clock:alarm.soundOnly', '正在播放提醒铃声...')}</span>
          )}
          {!enableSound && enableVibration && (
            <span>📳 {t('v3/clock:alarm.vibrationOnly', '正在震动提醒...')}</span>
          )}
          {!enableSound && !enableVibration && (
            <span>⏰ {t('v3/clock:alarm.noNotification', '计时已结束')}</span>
          )}
        </div>
      </div>
    </IOSModal>
  );
};

export default TimerAlarmModal;
